import classNames from "classnames";
import SVGIcon from "components/v2/Images/SVGIcon";
import ModalButtonTertiary from "components/v2/Modal/ModalFooter/ModalButtonTertiary";
import React, { useCallback, useState } from "react";

import Modal from "../../Modal";
import ModalBody from "../../Modal/ModalBody";
import ModalFooter from "../../Modal/ModalFooter";
import ModalButtonPrimary from "../../Modal/ModalFooter/ModalButtonPrimary";
import ModalButtonSecondary from "../../Modal/ModalFooter/ModalButtonSecondary";
import ModalTitle from "../../Modal/ModalTitle";
import Spin from "../../Spin";
import styles from "./style.module.css";

const ConfirmModal = ({
  children,
  onClose,
  title = "Confirm",
  modalBodyClassName = "",
  primaryButtonText = "Confirm",
  primaryButtonClassName = "",
  onPrimaryButtonClick,
  primaryButtonDisabled = false,
  secondaryButtonText = "Cancel",
  isSecondaryButtonVisible = true,
  onSecondaryButtonClick,
  secondaryButtonClassName = "",
  containerClassName = "",
  closeOnOutsideClick = false,
  modalTitleClassName = null,
  headerIcon = null,
  headerIconClassName = null,
  footerClassName = null,
  tertiaryButtonText = "Back",
  isTertiaryButtonVisible = false,
  onTertiaryButtonClick,
  tertiaryButtonClassName = "",
  headerIconComponent = null,
  showCloseIcon = true,
  titleComponent = null,
}) => {
  const [isPrimaryButtonLoading, setIsPrimaryButtonLoading] = useState(false);
  const [isSecondaryButtonLoading, setIsSecondaryButtonLoading] = useState(false);

  const onCancel = useCallback(
    async (e) => {
      try {
        e?.stopPropagation();
        if (onClose) {
          await onClose();
        }
      } catch (error) {
        console.error(error);
      }
    },
    [onClose],
  );

  const onClick = useCallback(
    async (e) => {
      try {
        e?.stopPropagation();
        setIsPrimaryButtonLoading(true);
        if (onPrimaryButtonClick) {
          await onPrimaryButtonClick();
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsPrimaryButtonLoading(false);
      }
    },
    [onPrimaryButtonClick],
  );

  const onSecondaryBtnClick = useCallback(
    async (e) => {
      try {
        e?.stopPropagation();
        setIsSecondaryButtonLoading(true);
        if (onSecondaryButtonClick) {
          await onSecondaryButtonClick();
        } else {
          onCancel();
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsSecondaryButtonLoading(false);
      }
    },
    [onCancel, onSecondaryButtonClick],
  );

  return (
    <Modal
      open={true}
      title=""
      footer={null}
      className={classNames(styles.container, containerClassName)}
      onCancel={onCancel}
      closeOnOutsideClick={closeOnOutsideClick}
      showCloseIcon={showCloseIcon}
    >
      <div data-testid={`ConfirmModal-${title}`}>
        <ModalTitle className={modalTitleClassName}>
          {headerIcon ? <SVGIcon src={headerIcon} className={headerIconClassName} /> : null}
          {headerIconComponent ? headerIconComponent : null}
          {title}
          {titleComponent ? titleComponent : null}
        </ModalTitle>
        <ModalBody className={modalBodyClassName}>{children}</ModalBody>
        {isTertiaryButtonVisible || isSecondaryButtonVisible || onPrimaryButtonClick ? (
          <ModalFooter className={classNames(styles.modalFooter, footerClassName)}>
            {isTertiaryButtonVisible ? (
              <ModalButtonTertiary
                dataTestId="ConfirmModal-button-tertiary"
                onClick={onTertiaryButtonClick}
                className={tertiaryButtonClassName}
              >
                {tertiaryButtonText}
              </ModalButtonTertiary>
            ) : null}
            {isSecondaryButtonVisible ? (
              <ModalButtonSecondary
                dataTestId="ConfirmModal-button-secondary"
                onClick={onSecondaryBtnClick}
                className={secondaryButtonClassName}
                disabled={isSecondaryButtonLoading}
              >
                {isSecondaryButtonLoading ? <Spin /> : null}
                {secondaryButtonText}
              </ModalButtonSecondary>
            ) : null}
            {onPrimaryButtonClick ? (
              <ModalButtonPrimary
                dataTestId={`ConfirmModal-button-${title}`}
                className={primaryButtonClassName}
                onClick={onClick}
                disabled={primaryButtonDisabled || isPrimaryButtonLoading}
              >
                {isPrimaryButtonLoading ? <Spin /> : null}
                {primaryButtonText}
              </ModalButtonPrimary>
            ) : null}
          </ModalFooter>
        ) : null}
      </div>
    </Modal>
  );
};

export default ConfirmModal;
